import { Box, Paper, Typography } from "@material-ui/core";
import { ArrowForward, FiberManualRecord } from "@material-ui/icons";
import { useEventCardStyles } from "@saleor/auditLogs/utils/eventCardStyles";
import React from "react";

import { LogisticsActivityLogs_logisticsActivityLogs_edges_node } from "../../types/LogisticsActivityLogs";
import {
  ExpressFleetUpdate,
  getTypeDisplayValue,
  isExpressFleetUpdate
} from "../../utils/expressFleetUtils";
import EventCardHeader from "../EventCardHeader";
import TimeContainer from "../TimeContainer";

interface ExpressFleetRateEventCardProps {
  event: LogisticsActivityLogs_logisticsActivityLogs_edges_node;
  eventNewValues: ExpressFleetUpdate;
  mallId?: string;
  filterBulkMallId?: string;
}

const ExpressFleetRateEventCard: React.FC<ExpressFleetRateEventCardProps> = props => {
  const { event, eventNewValues, mallId, filterBulkMallId } = props;
  const classes = useEventCardStyles({});

  const [showActivity, setShowActivity] = React.useState(true);

  let eventName = "";
  let eventParameter: { old: {}; new: {} };
  let eventMalls: string[] = [];

  if (isExpressFleetUpdate(eventNewValues)) {
    eventParameter = eventNewValues.parameters;
    if (mallId || filterBulkMallId) {
      eventMalls = event.malls
        .filter(mall => mall.id === mallId || mall.id === filterBulkMallId)
        .map(mall => mall.name);
    } else {
      eventMalls = event.malls.map(mall => mall.name);
    }
    eventName = eventMalls.length === 1 ? eventMalls[0] : "";
    if (mallId) {
      eventName = getTypeDisplayValue(event.type);
    }
  }

  const hideEventItems = event.action.includes("DELETE");

  const renderStatus = parameter => {
    if (parameter?.old?.is_enabled === parameter?.new?.is_enabled) {
      return null;
    }

    return (
      <li>
        <div className={classes.rowLi}>
          <span
            className={`${classes.rowLabel} ${classes.rowLabelTextGray} ${classes.rowLabelMinWidthShort}`}
          >
            Autobooking:
          </span>
          <span className={classes.textGray}>
            {parameter?.old?.is_enabled ? "ACTIVE" : "INACTIVE"}
          </span>{" "}
          <ArrowForward className={classes.rowArrowIcon} />{" "}
          {parameter?.new?.is_enabled ? "ACTIVE" : "INACTIVE"}{" "}
        </div>
      </li>
    );
  };

  const renderCourier = parameter => {
    if (parameter?.old?.courier === parameter?.new?.courier) {
      return null;
    }

    return (
      <li>
        <div className={classes.rowLi}>
          <span
            className={`${classes.rowLabel} ${classes.rowLabelTextGray} ${classes.rowLabelMinWidthShort}`}
          >
            Assigned Courier:
          </span>

          <>
            <span className={classes.textGray}>{parameter?.old?.courier}</span>{" "}
            <ArrowForward className={classes.rowArrowIcon} />{" "}
            {parameter?.new?.courier}
          </>
        </div>
      </li>
    );
  };

  const renderAllMinutes = parameter => (
    <>
      {renderMinutes(
        "Any Status (mins.):",
        parameter?.old?.any_status_minutes,
        parameter?.new?.any_status_minutes
      )}
      {renderMinutes(
        "Ready Status (mins.):",
        parameter?.old?.ready_status_minutes,
        parameter?.new?.ready_status_minutes
      )}
      {renderMinutes(
        "Collected Status (mins.):",
        parameter?.old?.collected_status_minutes,
        parameter?.new?.collected_status_minutes
      )}
    </>
  );

  const renderMinutes = (label: string, old_value, new_value) => {
    if (old_value === new_value) {
      return null;
    }

    return (
      <li>
        <div className={classes.rowLi}>
          <span
            className={`${classes.rowLabel} ${classes.rowLabelTextGray} ${classes.rowLabelMinWidthLong}`}
          >
            {label}
          </span>

          <>
            <span className={classes.textGray}>{old_value}</span>{" "}
            <ArrowForward className={classes.rowArrowIcon} /> {new_value}
          </>
        </div>
      </li>
    );
  };

  return (
    <Box className={classes.timelineBox}>
      <Box className={classes.timelineContainer}>
        <FiberManualRecord className={`${classes.dot} [status]`} />
        <Box className={classes.timelineItem}>
          <TimeContainer
            dateTimeStamp={event.createdAt}
            eventStatus={""}
            eventStatusClass={""}
            showDot={false}
          />

          <div style={{ width: "100%" }}>
            <EventCardHeader
              eventName={eventMalls.length === 1 ? eventName : ""}
              eventUser={event?.doer?.email}
              eventUserActionType={
                event.action.includes("CREATE") ? "created by" : "updated by"
              }
              showEventItems={!hideEventItems}
              showActivity={showActivity}
              action={() => setShowActivity(!showActivity)}
            />

            {showActivity && (
              <>
                {eventMalls &&
                  eventMalls.length > 0 &&
                  eventMalls.map((mall, index) => (
                    <>
                      {eventMalls.length > 1 && (
                        <Typography
                          key={`auditLogsExpressFleetEventItemCardMall-${index}`}
                          variant="subtitle1"
                          className={classes.eventName}
                        >
                          {mall}
                        </Typography>
                      )}

                      <Paper
                        className={classes.paper}
                        key={`auditLogsExpressFleetEventItemCard-${index}`}
                      >
                        <div className={classes.updateRow}>
                          {!mallId && (
                            <>
                              {event.type === "FOOD_AND_BEVERAGES" ? (
                                <div
                                  className={`${classes.rowBadge} ${classes.rowBadgeCustomOrange}`}
                                >
                                  {getTypeDisplayValue(event.type)}
                                </div>
                              ) : event.type === "GENERAL_MERCHANDISE" ? (
                                <div
                                  className={`${classes.rowBadge} ${classes.rowBadgeCustomPurple}`}
                                >
                                  {getTypeDisplayValue(event.type)}
                                </div>
                              ) : (
                                <></>
                              )}
                            </>
                          )}

                          <Typography>
                            <ul>
                              {renderStatus(eventParameter)}
                              {renderCourier(eventParameter)}
                              {renderAllMinutes(eventParameter)}
                            </ul>
                          </Typography>
                        </div>
                      </Paper>
                    </>
                  ))}
              </>
            )}
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default ExpressFleetRateEventCard;
